import React from 'react';
import classNames from 'classnames';
import { motion } from 'framer-motion';

import { IHealthMap } from '../../api/charts/soulMatrix';

import { ChakraId } from './ChakraTypes';
import styles from './HealthMap.module.scss';

export interface HealthMapRow {
  id: ChakraId;
  physical: number;
  energy: number;
  emotions: number;
  highlight?: boolean;
}

interface HealthMapProps {
  data: IHealthMap;
  highlightedIDs: number[];
}

type ColumnId = keyof HealthMapRow | 'chakraTitle';

interface ColumnConfig {
  id: ColumnId;
  label: string;
  render?: (row: HealthMapRow) => React.ReactNode;
}

const CHAKRA_TITLES: Record<ChakraId, string> = {
  sahasrara: 'Сахасрара',
  ajna: 'Аджна',
  vishuddha: 'Вишудха',
  anahata: 'Анахата',
  manipura: 'Манипура',
  svadhisthana: 'Свадхистана',
  muladhara: 'Муладхара',
  total: 'Итог',
};

const CHAKRA_ID_TO_POSITIONS: Record<ChakraId, number[]> = {
  sahasrara: [46, 47, 48],
  ajna: [49, 50, 51],
  vishuddha: [52, 53, 54],
  anahata: [55, 56, 57],
  manipura: [58, 59, 60],
  svadhisthana: [61, 62, 63],
  muladhara: [64, 65, 66],
  total: [67, 68, 69],
};

const tbodyVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};

const rowVariants = {
  hidden: { opacity: 0, x: -12 },
  visible: { opacity: 1, x: 0 },
};

export function HealthMap({ data, highlightedIDs }: HealthMapProps) {
  const isHighlighted = (id: ChakraId): boolean => {
    // Get positions for the given chakra ID
    const chakraPositions = CHAKRA_ID_TO_POSITIONS[id];

    // If no positions are found, it's not highlighted
    if (!chakraPositions) {
      return false;
    }

    // Check if all positions are present in the highlightedIDs
    return chakraPositions.every(position => highlightedIDs.includes(position));
  };

  const columns: ColumnConfig[] = [
    {
      id: 'chakraTitle',
      label: 'Название чакры',
      render: row => (
        <>
          <span className={styles.colorIndicator} style={{ backgroundColor: getChakraColor(row.id) }} />
          {CHAKRA_TITLES[row.id]}
        </>
      ),
    },
    { id: 'physical', label: 'Физика' },
    { id: 'energy', label: 'Энергия' },
    { id: 'emotions', label: 'Эмоции' },
  ];

  const rows: HealthMapRow[] = [
    {
      id: 'sahasrara',
      physical: data.sahasrara.physics,
      energy: data.sahasrara.energy,
      emotions: data.sahasrara.emotions,
    },
    {
      id: 'ajna',
      physical: data.ajna.physics,
      energy: data.ajna.energy,
      emotions: data.ajna.emotions,
    },
    {
      id: 'vishuddha',
      physical: data.vishuddha.physics,
      energy: data.vishuddha.energy,
      emotions: data.vishuddha.emotions,
    },
    {
      id: 'anahata',
      physical: data.anahata.physics,
      energy: data.anahata.energy,
      emotions: data.anahata.emotions,
      highlight: true,
    },
    {
      id: 'manipura',
      physical: data.manipura.physics,
      energy: data.manipura.energy,
      emotions: data.manipura.emotions,
    },
    {
      id: 'svadhisthana',
      physical: data.svadhisthana.physics,
      energy: data.svadhisthana.energy,
      emotions: data.svadhisthana.emotions,
    },
    {
      id: 'muladhara',
      physical: data.muladhara.physics,
      energy: data.muladhara.energy,
      emotions: data.muladhara.emotions,
    },
    {
      id: 'total',
      physical: data.total.physics,
      energy: data.total.energy,
      emotions: data.total.emotions,
    },
  ];
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th className={styles.firstTh}></th>
          {columns.map(column => (
            <th key={column.id}>{column.label}</th>
          ))}
        </tr>
      </thead>
      <motion.tbody variants={tbodyVariants} initial="hidden" animate="visible">
        {rows.map(row => (
          <motion.tr
            variants={rowVariants}
            id={row.id}
            key={row.id}
            className={classNames({ [styles.highlight]: isHighlighted(row.id) })}
          >
            <td style={{ backgroundColor: getChakraColor(row.id) }}></td>

            {columns.map(column => {
              const content = column.render ? column.render(row) : row[column.id as keyof HealthMapRow];
              return (
                <td key={column.id} className={column.id === 'chakraTitle' ? styles.chakraColumn : undefined}>
                  {content}
                </td>
              );
            })}
          </motion.tr>
        ))}
      </motion.tbody>
    </table>
  );
}

function getChakraColor(id: ChakraId): string {
  const colors: Record<ChakraId, string> = {
    sahasrara: '#B399FF',
    ajna: '#7986CB',
    vishuddha: '#4FC3F7',
    anahata: '#AED581',
    manipura: '#FFD54F',
    svadhisthana: '#FFB74D',
    muladhara: '#E57373',
    total: '#5F5C54',
  };
  return colors[id];
}
